import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';

import { createContext, useSyncExternalStore } from 'react';

import { UseQueryOptions } from '_lib/api/apiResourceFactory';
import { ourQueryClient, CACHE_PREFIX } from '_lib/api/QueryProvider';

import { LocalStorageItem } from '_lib/localStorage/localStorageItem';

import { API } from '../../api/apiService';
import {
  AccountResponseDTO,
  AppNameEnum,
  CallCenterDetailDTO,
  ChangePasswordDTO,
  Company,
  LoginDTO,
  UserDetailDTO
} from '../../api/apiTypes.shim';
import { AuthStorage } from '../../localStorage/authStorage';
import { reportException, setReportingUser } from '../../utils/sentryService';

import { AuthTokenNotFound, AuthUser, LoginResult } from './auth.types';

import type { AxiosRequestConfig } from 'axios';

/**
 * Auth caches are stale when 30 mins old
 */
const DEFAULT_AUTH_CACHE_STALE_TIME = 1000 * 60 * 30;

/**
 * Any inactive auth cache data is garbage collected in 24 hours
 */
export const DEFAULT_AUTH_CACHE_GC_TIME = 1000 * 60 * 60 * 24;

function cacheOptions<T>(resourceUri: string, id: string | null | undefined, queryConfig?: UseQueryOptions<T>) {
  return queryOptions({
    queryKey: [CACHE_PREFIX, resourceUri, id],
    queryFn: id
      ? async () => {
          try {
            const result = await API.get<T>(`${resourceUri}/${id}`);
            return result.data;
          } catch (err) {
            reportException(err);
            throw err;
          }
        }
      : skipToken,
    gcTime: id ? DEFAULT_AUTH_CACHE_GC_TIME : 0,
    staleTime: DEFAULT_AUTH_CACHE_STALE_TIME,
    ...queryConfig
  });
}

const getContainedCacheResource = <T extends object>(
  resourceUri: string | (() => string),
  idItem: LocalStorageItem<string>
) => {
  const getResourceUri = () => (typeof resourceUri === 'string' ? resourceUri : resourceUri());

  const removeId = (id: string, queryConfig?: UseQueryOptions<T>) => {
    ourQueryClient.removeQueries({
      queryKey: cacheOptions<T>(getResourceUri(), id, queryConfig).queryKey,
      exact: true
    });
  };

  return {
    useCacheQuery: (queryConfig?: UseQueryOptions<T>) => {
      /** This React hook re-runs our query on changes to the value of the idItem (aka 'id') */
      const id = useSyncExternalStore(idItem.subscribe, idItem.get);

      return useQuery(cacheOptions<T>(getResourceUri(), id, queryConfig));
    },
    get: (queryConfig?: UseQueryOptions<T>) =>
      idItem.get()
        ? ourQueryClient.fetchQuery(cacheOptions<T>(getResourceUri(), idItem.get(), queryConfig))
        : Promise.resolve(null),
    set: async (idOrValue: string | (T & { id: string })) => {
      const oldId = idItem.get();
      if (oldId && oldId !== (typeof idOrValue === 'string' ? idOrValue : idOrValue.id)) removeId(oldId);

      if (typeof idOrValue === 'string') {
        idItem.set(idOrValue);
        await ourQueryClient.fetchQuery(cacheOptions<T>(getResourceUri(), idOrValue));
      } else {
        idItem.set(idOrValue.id);
        ourQueryClient.setQueryData(cacheOptions<T>(getResourceUri(), idOrValue.id).queryKey, idOrValue);
      }
    },
    remove: (queryConfig?: UseQueryOptions<T>) => {
      const id = idItem.get();
      if (id) removeId(id, queryConfig);
      idItem.set(null);
    }
  };
};

// NOTE - must support a dynamic cache resource uri, since at this time 'account users' make requests to '/account-user/id'
// and 'users' make requests to '/user/id'
const authUserCacheResource = getContainedCacheResource<AuthUser>(
  () => AuthStorage.currentUserVariant.get() || 'user',
  AuthStorage.currentUserId
);
const accountCacheResource = getContainedCacheResource<AccountResponseDTO>('account', AuthStorage.currentAccountId);
const callCenterCacheResource = getContainedCacheResource<CallCenterDetailDTO>(
  'call-center',
  AuthStorage.currentCallCenterId
);
const companyCacheResource = getContainedCacheResource<Company>('company', AuthStorage.currentCompanyId);

const setAuthCaches = async (authUser: AuthUser) => {
  if (authUser.accountId) {
    AuthStorage.currentUserVariant.set('account-user');
  } else {
    AuthStorage.currentUserVariant.set('user');
  }

  const authPromises = [authUserCacheResource.set(authUser)];
  if (authUser.accountId) {
    authPromises.push(accountCacheResource.set(authUser.accountId));
  }
  if (authUser.callCenterId) {
    authPromises.push(callCenterCacheResource.set(authUser.callCenterId));
  }
  const results = await Promise.allSettled(authPromises);
  results.forEach((result) => result.status === 'rejected' && reportException(result.reason, { extra: { authUser } }));
};

async function forgotPassword(data: { appName: AppNameEnum; email: string }): Promise<void> {
  await API.post<void>('forgotPassword', data);
}

async function login(data: LoginDTO): Promise<LoginResult> {
  const res = await API.post<UserDetailDTO>('login', data);

  const authToken = res.headers.authorization;
  if (!authToken) {
    const { appName, email } = data;
    const error = new AuthTokenNotFound(`Login (${appName}): No auth token found!`, {
      messageToDisplay: 'Unexpected Error occurred for login. Development team has been notified'
    });
    reportException(error, { extra: { appName, email, resData: res.data } });
    throw error;
  }

  AuthStorage.authToken.set(authToken);

  await setAuthCaches(res.data);
  setReportingUser(res.data);

  return { user: res.data };
}

function logout() {
  ourQueryClient.clear();
  AuthStorage.authToken.set(null);

  AuthStorage.currentUserVariant.set(null);

  authUserCacheResource.remove();
  accountCacheResource.remove();
  callCenterCacheResource.remove();
  companyCacheResource.remove();

  setReportingUser(null);
}

async function resetPassword(
  resetToken: string,
  data: ChangePasswordDTO,
  appName: AppNameEnum,
  requestOptions?: AxiosRequestConfig['extraOptions']
): Promise<LoginResult> {
  const res = await API.put<UserDetailDTO>(`resetPassword/${resetToken}`, data, { extraOptions: requestOptions });

  const authToken = res.headers?.authorization || null;
  if (!authToken) {
    const error = new AuthTokenNotFound(`ResetPassword (${appName}): No auth token found!`, {
      messageToDisplay: 'Unexpected Error occurred for password reset. Development team has been notified'
    });
    reportException(error, { extra: { appName, resData: res.data } });
    throw error;
  }

  AuthStorage.authToken.set(authToken);

  await setAuthCaches(res.data);
  setReportingUser(res.data);

  return { user: res.data };
}

const setAuthToken = (authToken: string) => {
  AuthStorage.authToken.set(authToken);
};

export const AuthResources = {
  authUserCacheResource,
  accountCacheResource,
  callCenterCacheResource,
  companyCacheResource,
  setAuthToken,
  setAuthCaches,
  login,
  logout,
  resetPassword,
  forgotPassword
} as const;

export type AuthResourceConfig = typeof AuthResources;

export const AuthContext = createContext(AuthResources);
